import React, { useState } from 'react';
import { Input } from '@/app/components/ui/input';
import { Flex, Text } from '@radix-ui/themes';
import { Portal } from 'react-portal';
import { cn } from '@/lib/utils';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import PasswordInput from '@/app/components/form/PasswordInput';

export type Criteria = {
  length: string | boolean;
  uppercase: string | boolean;
  number: string | boolean;
};

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  criteria: Criteria
}

const passwordCriteria: Criteria = {
  length: "Be at least 8 characters",
  uppercase: "Include at least one uppercase letter",
  number: "Include at least one number"
}

const SignupPasswordInput = React.forwardRef<HTMLInputElement, CustomInputProps>(({ criteria, ...props }, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const handleFocus = (e: any) => {
    const rect = e.target.getBoundingClientRect();
    setPopupPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className="relative rounded-md drop-shadow-xl">
      <PasswordInput
        ref={ref}
        {...props}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {isFocused && (
        <Portal>
          <Flex
            direction="column"
            gap="2"
            style={{ position: 'absolute', top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}
            className={cn("mt-2 p-4 pr-6 border rounded bg-white shadow-lg z-50")}
          >
            <Text className="font-semibold pb-2 text-sm">Password must:</Text>
            {Object.entries(criteria).map(([criterion, value]) => (
              <Flex key={criterion} align="center" className="space-x-3">
                {value ? (
                  <CheckCircledIcon className="text-green-600" />
                ) : (
                  <CrossCircledIcon className="text-red-600" />
                )}
                <Text className="text-sm">{passwordCriteria[criterion as keyof Criteria]}</Text>
              </Flex>
            ))}
          </Flex>
        </Portal>
      )}
    </div>
  );
})
SignupPasswordInput.displayName = "SignupPasswordInput";

export default SignupPasswordInput;
